import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Maximize2 } from "lucide-react";
import { WORK, WORK_FILTERS, COPY } from "@/lib/site";
import { Reveal } from "./Motion";
import { Img } from "./Img";
import { Lightbox } from "./Lightbox";

export const Work = () => {
  const [filter, setFilter] = useState("All");
  const [lb, setLb] = useState(null);
  const items = WORK.filter((w) => filter === "All" || w.category === filter);

  return (
    <section id="work" data-testid="work-section" className="bg-surface py-24 md:py-36">
      <div className="container-editorial">
        <Reveal className="max-w-3xl">
          <p className="kicker mb-6">{COPY.work.kicker}</p>
          <h2 className="font-display text-5xl md:text-7xl leading-[0.95] tracking-tight">
            {COPY.work.heading}
          </h2>
          <p className="mt-6 text-base md:text-lg font-light text-ink/70 leading-relaxed">
            {COPY.work.sub}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10 flex flex-wrap gap-2.5">
          {WORK_FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`work-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setFilter(f)}
              className={`px-5 py-2.5 rounded-full text-[12px] font-medium tracking-[0.06em] transition-colors duration-300 border ${
                filter === f
                  ? "bg-ink text-beige border-ink"
                  : "bg-transparent text-ink/70 border-line hover:border-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </Reveal>

        <motion.div
          layout
          className="mt-12 columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6 [column-fill:_balance]"
        >
          <AnimatePresence mode="popLayout">
            {items.map((w, i) => (
              <motion.figure
                layout
                key={w.img}
                data-testid={`work-item-${w.img}`}
                onClick={() => setLb(i)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setLb(i);
                  }
                }}
                role="button"
                tabIndex={0}
                aria-label={`View ${w.category} image full screen`}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="group relative mb-5 md:mb-6 break-inside-avoid overflow-hidden rounded-sm bg-ink/5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ink"
              >
                <div
                  className={
                    w.span === "tall"
                      ? "aspect-[3/4.4]"
                      : w.span === "medium"
                        ? "aspect-[4/4.7]"
                        : "aspect-[4/5]"
                  }
                >
                  <Img
                    name={w.img}
                    alt={w.alt}
                    position="center 25%"
                    imgClassName="w-full h-full transition-transform [transition-duration:1400ms] ease-out group-hover:scale-[1.06]"
                  />
                </div>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/55 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <span className="pointer-events-none absolute top-4 right-4 inline-flex items-center justify-center w-10 h-10 rounded-full bg-cream/90 text-ink translate-y-1 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <Maximize2 size={16} />
                </span>
                <figcaption className="absolute left-4 bottom-4 right-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="kicker !text-beige/90">{w.category}</span>
                  <p className="mt-1 text-sm text-beige leading-snug">{w.caption}</p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <Lightbox
        items={items}
        index={lb}
        onClose={() => setLb(null)}
        onNavigate={setLb}
      />
    </section>
  );
};
